<template>
  <div class="container h-100 p-0">
    <div class="row d-flex flex-column h-100">
      <div class="col mt-2 flex-grow-0">
        <ToggleGroup
          width="50%"
          :options="[
            { key: 'Daten', label: 'Daten', available: true },
            { key: 'Bild', label: 'Bild', available: !!cabin?.layout },
          ]"
          :select="selectCabinDetailDisplay"
          :selected-key="selectedCabinDetailDisplayKey"
        />
      </div>

      <div 
        v-if="selectedCabinDetailDisplayKey === 'Daten'"
        class="col mt-3 align-self-stretch"
      >
        <div
          v-for="property in propertyInfoList"
          :key="property.itemName"
          class="row mx-1 cabin-stats"
        >
          <div class="col-6 py-1 ps-0">
            {{ property.itemName }}
          </div>
          <div class="col-6 py-1 px-0 text-end">
            {{ property.itemVal }}
          </div>
        </div>
      </div>

      <div 
        v-else-if="selectedCabinDetailDisplayKey !== 'Daten' && !!cabin?.layout"
        class="col mt-3 image-container" 
      >
        <div 
          class="position-relative"
          @click.stop="toggleModal()"
        >
          <img
            class="img-style"
            :src="cabin.layout"
          >
          <i class="fa fa-search-plus position-absolute magnifying-glass" />
        </div>
        <Modal
          :show="modalIsOpen"
          :close="toggleModal"
          size="medium"
        >
          <div
            class="d-flex justify-content-center modal-image-container"
            @click="toggleModal"
          >
            <img 
              class="img-style" 
              :src="cabin.layout"
            >
          </div>
        </Modal>
      </div>

      <div class="col align-self-end flex-grow-0 pb-3">
        <div 
          v-if="!!cabin?.isAccessible" 
          class="row"
        >
          <div class="col fw-bold mt-3 lh-base">
            Barrierearme Kabinen sind nur telefonisch unter +49 381 20 27 07 07
            buchbar.
          </div>
        </div>
        <div class="row">
          <div class="col mt-3 mb-2">
            <Price 
              :amount="price" 
              class="pricing fw-bold lh-small"
            />
            <span class="fs-10">Kabinenpreis</span>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <ExpiButton 
              label-id="Kabine speichern"
              :is-disabled="!cabin || cabin.isAccessible"
              :click="clickSave"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ExpiButton from '@/components/common/ExpiButton.vue'
import Price from '@/components/common/Price.vue'
import { IPropertyInfo } from '@/components/interfaces'
import { useCabinStore, useUiStore } from '@/store'
import { take } from 'lodash-es'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import Modal from '../../common/Modal.vue'
import ToggleGroup from '../../common/ToggleGroup.vue'

export default defineComponent({
  components: { Modal, ToggleGroup, ExpiButton, Price },

  data: () => {
    return {
      toggleButtonKeys: ['Daten', 'Bild'],
      selectedCabinDetailDisplayKey: 'Daten',
      modalIsOpen: false,
    }
  },

  computed: {
    ...mapState(useCabinStore, {
      category: store => store.category,
      cabin: store => store.cabin,
    }),
    ...mapWritableState(useCabinStore, {
      selectedByUser: 'selectedByUser'
    }),

    price() {
      return this.category?.price
    },

    propertyInfoList() {
      const clist: IPropertyInfo[] = []

      if (!this.cabin) {
        return []
      }

      if (this.cabin.area !== 0) {
        clist.push({ itemName: 'Größe', itemVal: this.cabin.area + ' m²' })
      }

      if (
        !!this.cabin.balconyType?.length &&
        !!this.cabin.balcony
      ) {
        clist.push({
          itemName: 'mit ' + this.cabin.balconyType[0].toUpperCase() + this.cabin.balconyType.slice(1) + 'balkon',
          itemVal: this.cabin.balcony + ' m²',
        })
      }

      clist.push({
        itemName: 'max. Personenanzahl',
        itemVal: this.cabin.maxPerson,
      })

      if (this.cabin.walkinWardrobe) {
        clist.push({ itemName: 'begehbarer Kleiderschrank', itemVal: 'Ja' })
      }

      if (this.cabin.twoBathrooms) {
        clist.push({ itemName: 'Badezimmer', itemVal: '2' })
      }

      const doubleBed = 'Doppelbett'
      if (this.cabin.doubleBed > 0) {
        clist.push({ itemName: doubleBed, itemVal: 'Ja' })
      } else if (this.cabin.sepBeds > 0) {
        const separateBeds = 'getrennte Betten'
        clist.push({ itemName: separateBeds, itemVal: 'Ja' })
      }

      if (this.cabin.sofaBed > 0) {
        clist.push({ itemName: 'Sofabetten', itemVal: this.cabin.sofaBed })
      }

      if (this.cabin.wallPullman > 0) {
        clist.push({
          itemName: 'Wandpullman Betten',
          itemVal: this.cabin.wallPullman,
        })
      }

      if (this.cabin.cailingPullman > 0) {
        clist.push({
          itemName: 'Deckenpullman Betten',
          itemVal: this.cabin.cailingPullman,
        })
      }

      clist.push({
        itemName: 'Babybett möglich',
        itemVal: this.cabin.babyBed ? 'Ja' : 'Nein',
      })

      clist.push({
        itemName: 'Bett in Fahrtrichtung',
        itemVal: this.cabin.directionOfTravel ? 'Ja' : 'Nein',
      })

      if (this.cabin.connectionDoor) {
        clist.push({
          itemName: 'Verbindungstür',
          itemVal: 'zu ' + this.cabin.connection,
        })
      }

      if (this.cabin.isAccessible) {
        clist.push({
          itemName: 'barrierearm',
          itemVal: 'Ja',
        })
      }

      if (
        this.cabin.closeToStairs ||
        this.cabin.closeToRestaurant ||
        this.cabin.closeToWellness
      ) {
        const brick = ['Treppen', 'Lift', 'Wellness', 'Restaurant']
        const bricks = []

        if (this.cabin.closeToStairs) {
          bricks.push(brick[0])
          bricks.push(brick[1])
        }
        if (this.cabin.closeToRestaurant) {
          bricks.push(brick[3])
        }
        if (this.cabin.closeToWellness) {
          bricks.push(brick[2])
        }

        let finalString
        if (bricks.length !== 1) {
          finalString = `in ${take(bricks, bricks.length - 1).join('-, ')} & ${bricks[bricks.length - 1]}nähe`
        } else {
          finalString = `in ${bricks[0]}nähe`
        }

        clist.push({ itemName: 'Lage', itemVal: finalString })
      }

      return clist
    },
  },

  methods: {
    ...mapActions(useUiStore, {
      setDetailsPanelActiveTab: 'setDetailsPanelActiveTab'
    }),

    toggleModal() {
      this.modalIsOpen = !this.modalIsOpen
    },
    selectCabinDetailDisplay(key: string) {
      this.selectedCabinDetailDisplayKey = key
    },
    clickSave() {
      this.selectedByUser = true
      this.setDetailsPanelActiveTab('transportation')
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.cabin-stats {
  border-bottom: 1px solid $color-expinet-gray;
}

.img-style {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.magnifying-glass {
  font-size: 20px;
  color: $color-expinet-gray;
  right: 0;
  bottom: 0;
}

.modal-image-container {
  max-height: 65vh;
}

.pricing {
  font-size: large;
}

.image-container {
  cursor: zoom-in
}
</style>
